import React, { useEffect, useMemo } from "react";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import VerticalStepper from "../stepper";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../Layout";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import { Button } from "primereact/button";
import PropTypes from "prop-types";
import useShareholdersForm from "./useShareholdersForm";
import ShareholderAddress from "./ShareholderAddress";
import ShareholderInformationWrapper from "./ShareholderInformationWrapper";
import ShareRegistry from "./share-registry/ShareRegistry";
import ShareholderAttachments from "./ShareholderAttachments";

const ShareholdersForm = ({
  setShowDialog,
  regData,
  setRegData,
  resetRegData,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    handleBack,
    setActiveIndex,
    activeIndex,
    btnToggle,
    setBtnToggle,
    formDetails,
    setFormDetails,
    isCongrats,
    setIsCongrats,
    isLoading,
    entity,
    handleShareholderSubmit,
    shareholderType,
    setShareholderType,
    addressData,
    setAddressData,
    shareRegistryData,
    setShareRegistryData,
    modalVisible,
    setModalVisible,
    transaction,
    setTransaction,
    formatDate,
    files,
    setFiles,
  } = useShareholdersForm({
    setShowDialog,
    regData,
    setRegData,
    resetRegData,
    isNewShareholder: true,
  });

  const hasEntityPlusAccess = entity?.active_services?.some(
    (e) => e.name === "Entities +",
  );

  const steps = [
    {
      label: "Shareholder Information",
      content: (
        <ShareholderInformationWrapper
          setBtnToggle={setBtnToggle}
          formDetails={formDetails}
          setFormDetails={setFormDetails}
          regData={regData}
          setRegData={setRegData}
          isEditing={true}
          isNewShareholder={true}
          shareholderType={shareholderType}
          setShareholderType={setShareholderType}
          resetRegData={resetRegData}
          entity={entity}
        />
      ),
    },
    ...(hasEntityPlusAccess
      ? [
          {
            label: "Address",
            content: (
              <ShareholderAddress
                addressData={addressData}
                setAddressData={setAddressData}
                setBtnToggle={setBtnToggle}
                type={shareholderType}
                isNewAddress={true}
                isEdit={true}
              />
            ),
          },
        ]
      : []),
    {
      label: "Share Registry",
      content: (
        <ShareRegistry
          shareRegistryData={shareRegistryData}
          setShareRegistryData={setShareRegistryData}
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          transaction={transaction}
          setTransaction={setTransaction}
          formatDate={formatDate}
          isCongrats={isCongrats}
          setIsCongrats={setIsCongrats}
          setActiveIndex={setActiveIndex}
          resetRegData={resetRegData}
          entityId={id}
        />
      ),
    },
    ...(hasEntityPlusAccess
      ? [
          {
            label: "Attachments",
            content: (
              <ShareholderAttachments
                isCongrats={isCongrats}
                setIsCongrats={setIsCongrats}
                files={files}
                setFiles={setFiles}
                entityData={entity}
                setActiveIndex={setActiveIndex}
                regData={regData}
                resetRegData={resetRegData}
                shareholderType={shareholderType}
              />
            ),
          },
        ]
      : []),
  ];

  const handleNext = () => {
    setActiveIndex((prevActiveStep) =>
      Math.min(prevActiveStep + 1, steps.length - 1),
    );
  };

  const breadcrumbs = useMemo(
    () => [
      {
        label: <span className="breadcrumb-link">My Entities</span>,
        command: () => navigate("/entities"),
        id: "entities",
      },
      {
        label: (
          <span className="breadcrumb-link">
            {entity?.name} (
            <ReactCountryFlag
              countryCode={entity?.country}
              svg
              className=""
            />{" "}
            {getName(entity?.country || "")})
          </span>
        ),
        id: "entity",
        command: () => navigate(`/entity/${id}/dashboard`),
      },
      {
        label: (
          <span className="breadcrumb-link">Appointments & Ownership</span>
        ),
        command: () =>
          navigate(`/entity/${id}/appointments-ownership?tab=shareholders`),
        id: "shareholder-data",
      },
      {
        label: "Add New Shareholder",
        id: "add-new-shareholder",
      },
    ],
    [entity, id, navigate],
  );

  const shareRegistryStepIndex = steps.findIndex(
    (step) => step.label === "Share Registry",
  );
  const hasNoShares = !shareRegistryData?.length;
  const isLastStepShareRegistry =
    activeIndex === steps.length - 1 &&
    steps[activeIndex]?.label === "Share Registry";
  const isConfirmDisabledDueToNoShares = isLastStepShareRegistry && hasNoShares;

  useEffect(() => {
    const isOnShareRegistryStep = activeIndex === shareRegistryStepIndex;
    if (isOnShareRegistryStep) {
      setBtnToggle(hasNoShares);
    }
  }, [activeIndex, shareRegistryData, shareRegistryStepIndex, hasNoShares]);

  return (
    <Layout crumbs={breadcrumbs}>
      <div className="grid shareholder-form">
        <div className="lg:col-3 md:col-12 sm:col-12 xs:col-12 pt-4">
          <Card className="card-onboard">
            <>
              <p className="add-new-entity-heading">Add New Shareholder</p>
              <p className="input-label m-0">
                Complete the necessary steps to onboard new Shareholder.
              </p>
            </>
            <Divider />
            <div className="step-menu">
              <VerticalStepper options={steps} activeIndex={activeIndex} />
            </div>
          </Card>
        </div>
        <div className="lg:col-9 md:col-12 sm:col-12 xs:col-12 pt-4">
          <Card className="card-onboard">{steps[activeIndex].content}</Card>
          {!isCongrats ? (
            <div className="next-btn-footer">
              <div className="d-flex justify-content-start">
                <Button
                  className="generic-cancel"
                  onClick={() => {
                    navigate(
                      `/entity/${id}/appointments-ownership?tab=shareholders`,
                    );
                  }}
                >
                  Cancel
                </Button>
              </div>
              <div className="flex justify-content-end gap-2">
                {activeIndex !== 0 ? (
                  <Button
                    onClick={handleBack}
                    disabled={activeIndex === 0}
                    className="cancel-btns"
                  >
                    Back
                  </Button>
                ) : (
                  ""
                )}
                {activeIndex === steps.length - 1 ? (
                  <Button
                    type="submit"
                    className={`add-new-contact confirm-appointment ${isConfirmDisabledDueToNoShares ? "disable" : ""}`}
                    onClick={handleShareholderSubmit}
                    loading={isLoading}
                    disabled={isConfirmDisabledDueToNoShares || isLoading}
                  >
                    Confirm Appointment
                  </Button>
                ) : (
                  <Button
                    disabled={btnToggle}
                    className={`add-new-contact ${btnToggle ? "disable" : null}`}
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
};

ShareholdersForm.propTypes = {
  setShowDialog: PropTypes.func,
  regData: PropTypes.object,
  setRegData: PropTypes.func,
  resetRegData: PropTypes.func,
};

export default ShareholdersForm;
